import {homepageCta} from './homepage-cta';

const CAPTURED_PAGE_CONTEXT_KEY = 'nsfwlandPageContext:';
const MAX_PENDING_CAPTURES = 100;
const CAPTURE_TTL_MS = 60000;
const CAPTURE_RETRY_MS = 100;
let pendingCaptures = [];
let captureRetryTimer = null;

const flushPendingCaptures = () => {
    captureRetryTimer = null;
    const now = Date.now();
    pendingCaptures = pendingCaptures.filter((item) => now - item.queuedAt < CAPTURE_TTL_MS);
    if (!pendingCaptures.length) return;

    if (!window.posthog || typeof window.posthog.capture !== 'function') {
        captureRetryTimer = setTimeout(flushPendingCaptures, CAPTURE_RETRY_MS);
        return;
    }

    const captures = pendingCaptures;
    pendingCaptures = [];
    captures.forEach(({eventName, properties}) => window.posthog.capture(eventName, properties));
};

const queueCapture = (eventName, properties) => {
    if (pendingCaptures.length >= MAX_PENDING_CAPTURES) pendingCaptures.shift();
    pendingCaptures.push({eventName, properties, queuedAt: Date.now()});
    if (captureRetryTimer === null) {
        captureRetryTimer = setTimeout(flushPendingCaptures, CAPTURE_RETRY_MS);
    }
    return true;
};

const capture = (eventName, properties = {}) => {
    if (!eventName || window.nsfwlandAnalytics?.enabled === false) return false;
    // Growtype Analytics owns and privacy-filters the PostHog SDK. Capture
    // through that configured instance so our small sanitized property set is
    // retained; its generic queue intentionally forwards event names only.
    if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(eventName, properties);
        return true;
    }
    // The child bundle can execute while PostHog's async SDK is still loading.
    // Keep the already-sanitized event briefly and deliver it to the configured
    // SDK once ready; the generic analytics queue intentionally drops custom
    // properties and is therefore not suitable for this site event contract.
    return queueCapture(eventName, properties);
};

const cleanLabel = (value, maxLength = 100) => String(value || '')
    .trim().replace(/[^a-zA-Z0-9._-]/g, '').substring(0, maxLength);

const capturePageContext = (context) => {
    if (!context?.event) return;
    const properties = {
        view_type: cleanLabel(context.contentType, 40),
        view_slug: cleanLabel(context.contentSlug, 100),
    };
    const key = `${CAPTURED_PAGE_CONTEXT_KEY}${context.event}:${properties.view_slug}`;
    try {
        if (window.sessionStorage.getItem(key) === '1') return;
    } catch (error) {
        // Session storage can be disabled by strict browser privacy settings.
    }
    if (!capture(context.event, properties)) return;
    try {
        window.sessionStorage.setItem(key, '1');
    } catch (error) {
        // The event was captured; storage is only used for deduplication.
    }
};

const bindExternalListingClicks = () => {
    document.querySelectorAll('.listing__link, .toptech-sidebar-external, .growth-listing__action').forEach((link) => {
        link.addEventListener('click', () => capture('external_listing_click', {
            surface: link.matches('.toptech-sidebar-external')
                ? 'listing_detail'
                : (link.matches('.growth-listing__action') ? 'acquisition_guide' : 'directory'),
        }));
    });
};

const bindDealEngagement = () => {
    document.querySelectorAll('.deal-card__action').forEach((link) => link.addEventListener('click', () => capture('deal_click')));
    document.querySelectorAll('.deal-card__source').forEach((link) => link.addEventListener('click', () => capture('deal_source_click')));
    document.querySelectorAll('[data-copy-code]').forEach((button) => button.addEventListener('click', () => capture('deal_code_copy')));
};

const bindEditorialEngagement = () => {
    document.querySelectorAll('article a[href], .toptech-editorial-layout a[href], .growth-related__links a, .growth-paths a').forEach((link) => {
        link.addEventListener('click', () => capture('editorial_link_click', {
            link_type: link.hostname && link.hostname !== window.location.hostname ? 'external' : 'internal',
        }));
    });
};

const captureSearch = (search) => {
    const params = new URLSearchParams(window.location.search);
    const isDirectorySearch = Boolean(params.get('q')?.trim());
    if (!search && !isDirectorySearch) return;
    const resultCount = search
        ? Number(search.resultCount || 0)
        : document.querySelectorAll('.listing__link').length;
    capture('site_search', {result_count: resultCount});
    if (resultCount === 0) capture('search_no_results');
};

const captureAcquisition = () => {
    const params = new URLSearchParams(window.location.search);
    const source = cleanLabel(params.get('utm_source'), 80);
    if (!source) return;
    capture('acquisition_landing', {
        source,
        medium: cleanLabel(params.get('utm_medium'), 40) || 'unknown',
        campaign: cleanLabel(params.get('utm_campaign'), 100) || 'unknown',
    });
};

function analytics() {
    const config = window.nsfwlandAnalytics || {};
    window.nsfwlandCapture = capture;
    if (config.enabled === false) return;
    if (config.homepageCta) {
        const attempt = (remaining) => {
            if (window.posthog) homepageCta(config.homepageCta, capture);
            else if (remaining > 0) setTimeout(() => attempt(remaining - 1), CAPTURE_RETRY_MS);
        };
        attempt(600);
    }
    capturePageContext(config.pageContext);
    captureSearch(config.search);
    captureAcquisition();
    bindExternalListingClicks();
    bindDealEngagement();
    bindEditorialEngagement();
}

export {analytics};
